import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const RELATIONSHIP_TYPES = ["Wife", "Husband", "Mom", "Dad", "Sister", "Brother", "Friend", "Colleague", "Child", "Grandparent", "Partner", "Other"];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const NOTES_MAX = 300;

export default function RelationshipDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    relationship_type: initial?.relationship_type || "Wife",
    birthday: initial?.birthday || "",
    anniversary: initial?.anniversary || "",
    gender: initial?.gender || "",
    picture: initial?.picture || "",
    fav_color: initial?.fav_color || "",
    fav_flower: initial?.fav_flower || "",
    fav_food: initial?.fav_food || "",
    budget: initial?.budget || 3000,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPicture = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload?folder=avatars", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("picture", res.data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.notes.length > NOTES_MAX) return;
    setSaving(true);
    try {
      const payload = { ...form, budget: parseInt(form.budget) || null };
      if (initial) await api.patch(`/relationships/${initial.id}`, payload);
      else await api.post("/relationships", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 pb-28 sm:pb-4 bg-charcoal/40 overflow-y-auto" onClick={onClose}
        data-testid="relationship-dialog">
        <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
          className="bg-white rounded-3xl max-w-2xl w-full max-h-[calc(100vh-9rem)] sm:max-h-[92vh] overflow-y-auto shadow-2xl my-auto">
          <header className="p-6 border-b border-warm flex items-center justify-between sticky top-0 bg-white z-10">
            <p className="font-display text-2xl text-charcoal">{initial ? "Edit person" : "Add person"}</p>
            <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen" data-testid="dialog-close"><X className="w-5 h-5" /></button>
          </header>
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="relative">
                {form.picture ? (
                  <img src={form.picture} className="w-20 h-20 rounded-full object-cover border border-warm" alt="" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-sage/20 flex items-center justify-center font-display text-2xl text-charcoal">
                    {(form.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-picture-btn"
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-terracotta text-white flex items-center justify-center border-2 border-white">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => uploadPicture(e.target.files?.[0])} />
              </div>
              <div className="flex-1">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(v) => update("name", v)} required testId="rd-name" />
              </div>
            </div>

            <Grid2>
              <div>
                <Label>Relationship *</Label>
                <select value={form.relationship_type} onChange={(e) => update("relationship_type", e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta"
                  data-testid="rd-type">
                  {RELATIONSHIP_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Gender</Label>
                <select value={form.gender} onChange={(e) => update("gender", e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta"
                  data-testid="rd-gender">
                  <option value="">—</option>
                  {GENDERS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </Grid2>

            <Grid2>
              <div>
                <Label>Birthday</Label>
                <Input type="date" value={form.birthday} onChange={(v) => update("birthday", v)} testId="rd-birthday" />
              </div>
              <div>
                <Label>Anniversary</Label>
                <Input type="date" value={form.anniversary} onChange={(v) => update("anniversary", v)} testId="rd-anniversary" />
              </div>
            </Grid2>

            <div className="pt-4 border-t border-warm">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-4">Preferences</p>
              <Grid2>
                <div>
                  <Label>Favorite color</Label>
                  <Input value={form.fav_color} onChange={(v) => update("fav_color", v)} placeholder="Red, ivory…" testId="rd-color" />
                </div>
                <div>
                  <Label>Favorite flower</Label>
                  <Input value={form.fav_flower} onChange={(v) => update("fav_flower", v)} placeholder="Roses, lilies…" testId="rd-flower" />
                </div>
                <div>
                  <Label>Favorite food</Label>
                  <Input value={form.fav_food} onChange={(v) => update("fav_food", v)} placeholder="Chocolate, biryani…" testId="rd-food" />
                </div>
                <div>
                  <Label>Typical budget (₹)</Label>
                  <Input type="number" value={form.budget} onChange={(v) => update("budget", v)} testId="rd-budget" />
                </div>
              </Grid2>
              <div className="mt-4">
                <Label>Anything we should know?</Label>
                <textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)}
                  className={`w-full rounded-xl bg-white border px-4 py-3 focus:outline-none resize-none ${form.notes.length > NOTES_MAX ? "border-rose-500 focus:border-rose-500" : "border-warm focus:border-terracotta"}`}
                  data-testid="rd-notes" placeholder="Allergies, food preferences, quirks, anything that'll help us make it special..." />
                <p className={`text-xs mt-1 text-right ${form.notes.length > NOTES_MAX ? "text-rose-600 font-semibold" : "text-charcoal/50"}`} data-testid="rd-notes-counter">
                  {form.notes.length}/{NOTES_MAX}
                </p>
              </div>
            </div>
          </div>
          <footer className="p-6 border-t border-warm flex justify-end gap-3 sticky bottom-0 bg-white">
            <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary" data-testid="rd-cancel">Cancel</button>
            <button type="submit" disabled={saving || form.notes.length > NOTES_MAX} className="pill-btn pill-btn-primary" data-testid="rd-save">
              {saving ? "Saving…" : initial ? "Save changes" : "Add person"}
            </button>
          </footer>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

const Label = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">{children}</label>
);

const Input = ({ value, onChange, type = "text", required, placeholder, testId }) => (
  <input type={type} required={required} value={value} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta" />
);

const Grid2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
